import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Modal } from '../components/common';
import adminService, { AdminUser, AdminStats } from '../services/admin.service';
import ejecutivoService from '../services/ejecutivo.service';
import { OdooSalesperson, Ejecutivo, EjecutivoConClientes, ClienteEjecutivo } from '../types';
import './AdminPanel.css';

interface ClientWithUser {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  hasUser: boolean;
  user?: AdminUser;
}

type TabType = 'clientes' | 'ejecutivos';

const AdminPanel: React.FC = () => {
  // Tab activa
  const [activeTab, setActiveTab] = useState<TabType>('clientes');

  // Estados principales
  const [clients, setClients] = useState<ClientWithUser[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const CLIENTS_PER_PAGE = 20;

  // Búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Sincronización
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ progress: 0, total: 0, new: 0 });
  const [lastSyncInfo, setLastSyncInfo] = useState<{ date: string | null; newClients: number } | null>(null);
  const syncPollRef = useRef<NodeJS.Timeout | null>(null);

  // Generación masiva de usernames
  const [isGeneratingUsernames, setIsGeneratingUsernames] = useState(false);

  // Modales
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientWithUser | null>(null);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
    mustChangeOnLogin: true
  });

  // ===== Estados para Ejecutivos =====
  const [ejecutivosConClientes, setEjecutivosConClientes] = useState<EjecutivoConClientes[]>([]);
  const [odooSalespeople, setOdooSalespeople] = useState<OdooSalesperson[]>([]);
  const [ejecutivosSearch, setEjecutivosSearch] = useState('');
  const [debouncedEjecutivosSearch, setDebouncedEjecutivosSearch] = useState('');
  const [isLoadingEjecutivos, setIsLoadingEjecutivos] = useState(false);
  const [isLoadingSalespeople, setIsLoadingSalespeople] = useState(false);

  // Modal para crear ejecutivo
  const [showCreateEjecutivoModal, setShowCreateEjecutivoModal] = useState(false);
  const [selectedSalesperson, setSelectedSalesperson] = useState<OdooSalesperson | null>(null);
  const [createEjecutivoForm, setCreateEjecutivoForm] = useState({
    name: '',
    email: '',
    password: ''
  });

  // Modal para cambiar contraseña de ejecutivo
  const [showEjecutivoPasswordModal, setShowEjecutivoPasswordModal] = useState(false);
  const [selectedEjecutivoConClientes, setSelectedEjecutivoConClientes] = useState<EjecutivoConClientes | null>(null);
  const [ejecutivoPasswordForm, setEjecutivoPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
    mustChangeOnLogin: true
  });

  // Modal para ver clientes de un ejecutivo
  const [showClientesModal, setShowClientesModal] = useState(false);
  const [clientesDelEjecutivo, setClientesDelEjecutivo] = useState<ClienteEjecutivo[]>([]);
  const [isLoadingClientes, setIsLoadingClientes] = useState(false);
  const [clientesTotalPages, setClientesTotalPages] = useState(1);
  const [clientesPage, setClientesPage] = useState(1);

  // Debounce para búsqueda de clientes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce para búsqueda de ejecutivos
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEjecutivosSearch(ejecutivosSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [ejecutivosSearch]);

  // Cargar estadísticas
  const loadStats = useCallback(async () => {
    try {
      const data = await adminService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Error cargando estadísticas:', err);
    }
  }, []);

  // Cargar clientes con usuarios
  const loadClients = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await adminService.getClientsWithUsers({
        search: debouncedSearch || undefined,
        page: currentPage,
        limit: CLIENTS_PER_PAGE
      });
      setClients(data.clients);
      setTotalClients(data.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar clientes');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, currentPage]);

  // Cargar info de última sincronización
  const loadSyncInfo = useCallback(async () => {
    try {
      const status = await adminService.getSyncStatus();
      // Usar lastSyncDate (persistido en BD) como fuente principal
      // completedAt es temporal y se pierde al reiniciar el servidor
      const syncDate = status.lastSyncDate || status.completedAt;
      if (syncDate) {
        setLastSyncInfo({
          date: syncDate,
          newClients: status.results?.new || 0
        });
      }
    } catch (err) {
      console.error('Error obteniendo info de sync:', err);
    }
  }, []);

  // ===== Funciones para Ejecutivos =====

  // Cargar ejecutivos con clientes de Odoo
  const loadEjecutivosConClientes = useCallback(async () => {
    try {
      setIsLoadingEjecutivos(true);
      const data = await ejecutivoService.getEjecutivosConClientes(debouncedEjecutivosSearch || undefined);
      setEjecutivosConClientes(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar ejecutivos');
    } finally {
      setIsLoadingEjecutivos(false);
    }
  }, [debouncedEjecutivosSearch]);

  // Cargar vendedores de Odoo (para crear cuentas de otros vendedores)
  const loadOdooSalespeople = useCallback(async () => {
    try {
      setIsLoadingSalespeople(true);
      const data = await ejecutivoService.getOdooSalespeople();
      setOdooSalespeople(data);
    } catch (err: any) {
      console.error('Error al cargar vendedores de Odoo:', err);
    } finally {
      setIsLoadingSalespeople(false);
    }
  }, []);

  // Cargar clientes de un ejecutivo específico
  const loadClientesDelEjecutivo = useCallback(async (ejecutivoId: number, page: number = 1) => {
    try {
      setIsLoadingClientes(true);
      const data = await ejecutivoService.getClientesByEjecutivo(ejecutivoId, { page, limit: 10 });
      setClientesDelEjecutivo(data.clientes);
      setClientesTotalPages(data.pagination.totalPages);
      setClientesPage(page);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar clientes');
    } finally {
      setIsLoadingClientes(false);
    }
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    loadStats();
    loadClients();
    loadSyncInfo();
  }, [loadStats, loadClients, loadSyncInfo]);

  // Cargar ejecutivos cuando cambia el tab o la búsqueda
  useEffect(() => {
    if (activeTab === 'ejecutivos') {
      loadEjecutivosConClientes();
    }
  }, [activeTab, loadEjecutivosConClientes]);

  // Cargar vendedores de Odoo cuando se abre el modal
  useEffect(() => {
    if (showCreateEjecutivoModal && odooSalespeople.length === 0) {
      loadOdooSalespeople();
    }
  }, [showCreateEjecutivoModal, odooSalespeople.length, loadOdooSalespeople]);

  // Polling para sincronización
  const checkSyncStatus = useCallback(async () => {
    try {
      const status = await adminService.getSyncStatus();
      setSyncProgress({
        progress: status.progress,
        total: status.total,
        new: status.results?.new || 0
      });

      if (!status.isRunning && isSyncing) {
        setIsSyncing(false);
        if (status.results) {
          setSuccessMessage(
            `Sincronización completada: ${status.results.new} clientes nuevos encontrados`
          );
          // Usar lastSyncDate como fuente principal (persistido en BD)
          const syncDate = status.lastSyncDate || status.completedAt;
          setLastSyncInfo({
            date: syncDate,
            newClients: status.results.new
          });
        }
        if (status.error) {
          setError('Error en sincronización: ' + status.error);
        }
        loadClients();
        loadStats();
      }
    } catch (err) {
      console.error('Error verificando estado de sync:', err);
    }
  }, [isSyncing, loadClients, loadStats]);

  useEffect(() => {
    if (isSyncing) {
      // Polling cada 5 segundos para no saturar el servidor
      syncPollRef.current = setInterval(checkSyncStatus, 5000);
    } else {
      if (syncPollRef.current) {
        clearInterval(syncPollRef.current);
        syncPollRef.current = null;
      }
    }
    return () => {
      if (syncPollRef.current) {
        clearInterval(syncPollRef.current);
      }
    };
  }, [isSyncing, checkSyncStatus]);

  // Handler: Sincronización delta (solo nuevos)
  const handleSyncNew = async () => {
    try {
      setIsSyncing(true);
      setSyncProgress({ progress: 0, total: 0, new: 0 });
      await adminService.syncOdooClients({ createUsers: true, deltaOnly: true });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al iniciar sincronización');
      setIsSyncing(false);
    }
  };

  // Handler: Generar usernames masivamente
  const handleGenerateMissingUsernames = async () => {
    try {
      setIsGeneratingUsernames(true);
      const result = await adminService.generateMissingUsernames();
      if (result.updated > 0) {
        setSuccessMessage(`Se generaron ${result.updated} usernames`);
        loadClients();
        loadStats();
      } else {
        setSuccessMessage('Todos los usuarios ya tienen username');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al generar usernames');
    } finally {
      setIsGeneratingUsernames(false);
    }
  };

  // Handler: Generar username
  const handleGenerateUsername = async (client: ClientWithUser) => {
    if (!client.user) return;
    try {
      const result = await adminService.generateUsername(client.user.id);
      setSuccessMessage(`Username generado: @${result.username}`);
      loadClients();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al generar username');
    }
  };

  // Handler: Cambiar contraseña
  const handleOpenPasswordModal = (client: ClientWithUser) => {
    setSelectedClient(client);
    setPasswordForm({
      newPassword: '',
      confirmPassword: '',
      mustChangeOnLogin: true
    });
    setShowPasswordModal(true);
  };

  const handleSavePassword = async () => {
    if (!selectedClient?.user) return;

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      await adminService.changeUserPassword(
        selectedClient.user.id,
        passwordForm.newPassword,
        passwordForm.mustChangeOnLogin
      );
      setSuccessMessage(`Contraseña de ${selectedClient.name} actualizada`);
      setShowPasswordModal(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cambiar contraseña');
    }
  };

  // Handler: Toggle acceso
  const handleToggleAccess = async (client: ClientWithUser) => {
    if (!client.user) return;
    try {
      await adminService.updateUser(client.user.id, {
        is_active: !client.user.is_active
      });
      setSuccessMessage(
        client.user.is_active
          ? `Acceso desactivado para ${client.name}`
          : `Acceso activado para ${client.name}`
      );
      loadClients();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cambiar acceso');
    }
  };

  // Handler: Crear acceso para cliente
  const handleCreateAccess = async (client: ClientWithUser) => {
    try {
      const result = await adminService.createUser({
        name: client.name,
        email: client.email || undefined,
        phone: client.phone || client.mobile || undefined,
        partner_id: client.id,
        generateUsernameFromName: true
      });
      const username = result.user.username ? `@${result.user.username}` : '';
      const tempPass = result.tempPassword || 'Temporal123!';
      setSuccessMessage(`Acceso creado: ${username} | Contraseña: ${tempPass}`);
      loadClients();
      loadStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al crear acceso');
    }
  };

  // ===== Handlers para Ejecutivos =====

  // Handler: Abrir modal para crear ejecutivo desde EjecutivoConClientes
  const handleOpenCreateEjecutivoFromOdoo = (ejecutivo: EjecutivoConClientes) => {
    setSelectedSalesperson({
      id: ejecutivo.odooUserId,
      name: ejecutivo.odooName,
      email: ejecutivo.odooEmail || '',
      login: ejecutivo.odooLogin || '',
      hasAccount: ejecutivo.hasAccount,
      clientesCount: ejecutivo.clientesCount
    } as OdooSalesperson & { clientesCount: number });
    setCreateEjecutivoForm({
      name: ejecutivo.odooName,
      email: '',
      password: ''
    });
    setShowCreateEjecutivoModal(true);
  };

  // Handler: Abrir modal para crear ejecutivo desde vendedor de Odoo
  const handleOpenCreateEjecutivoModal = (salesperson: OdooSalesperson) => {
    setSelectedSalesperson(salesperson);
    setCreateEjecutivoForm({
      name: salesperson.name,
      email: salesperson.email || '',
      password: ''
    });
    setShowCreateEjecutivoModal(true);
  };

  // Handler: Crear ejecutivo
  const handleCreateEjecutivo = async () => {
    if (!selectedSalesperson) return;

    try {
      const result = await ejecutivoService.createEjecutivo({
        odoo_user_id: selectedSalesperson.id,
        name: selectedSalesperson.name, // Usar nombre de Odoo directamente
        password: createEjecutivoForm.password.trim() || undefined
      });
      const username = result.ejecutivo.username ? `@${result.ejecutivo.username}` : '';
      const tempPass = result.tempPassword || createEjecutivoForm.password.trim() || 'Temporal123!';
      setSuccessMessage(`Ejecutivo creado - Usuario: ${username} | Contraseña: ${tempPass}`);
      setShowCreateEjecutivoModal(false);
      loadEjecutivosConClientes();
      loadOdooSalespeople();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al crear ejecutivo');
    }
  };

  // Handler: Toggle estado de ejecutivo
  const handleToggleEjecutivoStatus = async (ejecutivo: EjecutivoConClientes) => {
    if (!ejecutivo.account) return;
    try {
      await ejecutivoService.updateEjecutivo(ejecutivo.account.id, {
        is_active: !ejecutivo.account.is_active
      });
      setSuccessMessage(
        ejecutivo.account.is_active
          ? `Ejecutivo ${ejecutivo.odooName} desactivado`
          : `Ejecutivo ${ejecutivo.odooName} activado`
      );
      loadEjecutivosConClientes();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cambiar estado');
    }
  };

  // Handler: Abrir modal de contraseña de ejecutivo
  const handleOpenEjecutivoPasswordModal = (ejecutivo: EjecutivoConClientes) => {
    setSelectedEjecutivoConClientes(ejecutivo);
    setEjecutivoPasswordForm({
      newPassword: '',
      confirmPassword: '',
      mustChangeOnLogin: true
    });
    setShowEjecutivoPasswordModal(true);
  };

  // Handler: Guardar contraseña de ejecutivo
  const handleSaveEjecutivoPassword = async () => {
    if (!selectedEjecutivoConClientes?.account) return;

    if (ejecutivoPasswordForm.newPassword !== ejecutivoPasswordForm.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (ejecutivoPasswordForm.newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      await ejecutivoService.changeEjecutivoPassword(
        selectedEjecutivoConClientes.account.id,
        ejecutivoPasswordForm.newPassword,
        ejecutivoPasswordForm.mustChangeOnLogin
      );
      setSuccessMessage(`Contraseña de ${selectedEjecutivoConClientes.odooName} actualizada`);
      setShowEjecutivoPasswordModal(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cambiar contraseña');
    }
  };

  // Handler: Ver clientes de un ejecutivo
  const handleViewClientes = async (ejecutivo: EjecutivoConClientes) => {
    if (!ejecutivo.account) return;
    setSelectedEjecutivoConClientes(ejecutivo);
    setShowClientesModal(true);
    await loadClientesDelEjecutivo(ejecutivo.account.id, 1);
  };

  // Limpiar mensajes
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Calcular páginas
  const totalPages = Math.ceil(totalClients / CLIENTS_PER_PAGE);

  // Total de ejecutivos para mostrar en el badge del tab
  const totalEjecutivos = ejecutivosConClientes.length;

  // Filtrar vendedores de Odoo que no tienen cuenta (excluyendo los que ya están en ejecutivosConClientes)
  const ejecutivoOdooIds = new Set(ejecutivosConClientes.map(e => e.odooUserId));
  const salespeopleWithoutAccount = odooSalespeople.filter(s => !s.hasAccount && !ejecutivoOdooIds.has(s.id));

  // Separar ejecutivos con y sin cuenta
  const ejecutivosConCuenta = ejecutivosConClientes.filter(e => e.hasAccount);
  const ejecutivosSinCuenta = ejecutivosConClientes.filter(e => !e.hasAccount);

  // Formatear fecha
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Nunca';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Detectar si el nombre parece un teléfono
  const isPhoneName = (name: string) => {
    if (!name) return true;
    // Si empieza con + o tiene más de 50% dígitos, es un teléfono
    const cleaned = name.replace(/\s/g, '');
    if (cleaned.startsWith('+')) return true;
    const digits = cleaned.replace(/\D/g, '').length;
    return digits > cleaned.length * 0.5;
  };

  // Obtener nombre para mostrar
  const getDisplayName = (client: ClientWithUser) => {
    if (!client.name || isPhoneName(client.name)) {
      // Si no tiene nombre real, mostrar "Cliente" + parte del teléfono o ID
      const phone = client.phone || client.mobile;
      if (phone) {
        const lastDigits = phone.replace(/\D/g, '').slice(-4);
        return `Cliente ****${lastDigits}`;
      }
      return `Cliente #${client.id}`;
    }
    return client.name;
  };

  // Obtener inicial para avatar
  const getAvatarInitial = (client: ClientWithUser) => {
    const displayName = getDisplayName(client);
    return displayName.charAt(0).toUpperCase();
  };

  return (
    <div className="admin-panel-v2">
      {/* Header */}
      <header className="admin-header-v2">
        <div className="header-title">
          <div className="title-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div>
            <h1>Panel de Administración</h1>
            <p className="header-subtitle">Gestión de clientes y accesos</p>
          </div>
        </div>
      </header>

      {/* Mensajes */}
      {successMessage && (
        <div className="toast toast-success">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {successMessage}
        </div>
      )}
      {error && (
        <div className="toast toast-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'clientes' ? 'active' : ''}`}
          onClick={() => setActiveTab('clientes')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 00-3-3.87m-4-12a4 4 0 010 7.75" />
          </svg>
          Clientes
        </button>
        <button
          className={`admin-tab ${activeTab === 'ejecutivos' ? 'active' : ''}`}
          onClick={() => setActiveTab('ejecutivos')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          Ejecutivos
          {totalEjecutivos > 0 && <span className="tab-badge">{totalEjecutivos}</span>}
        </button>
      </div>

      {/* ===== TAB: CLIENTES ===== */}
      {activeTab === 'clientes' && (
        <>
      {/* Stats Dashboard */}
      <section className="stats-dashboard">
        <div className="stat-card-v2 primary">
          <div className="stat-content">
            <span className="stat-number">{stats?.totalUsers || 0}</span>
            <span className="stat-label">Total Usuarios</span>
          </div>
          <div className="stat-icon-bg">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 00-3-3.87m-4-12a4 4 0 010 7.75" />
            </svg>
          </div>
        </div>

        <div className="stat-card-v2 success">
          <div className="stat-content">
            <span className="stat-number">{stats?.activeUsers || 0}</span>
            <span className="stat-label">Activos</span>
          </div>
          <div className="stat-icon-bg">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22,4 12,14.01 9,11.01" />
            </svg>
          </div>
        </div>

        <div className="stat-card-v2 warning">
          <div className="stat-content">
            <span className="stat-number">{stats?.recentLogins || 0}</span>
            <span className="stat-label">Logins (7 días)</span>
          </div>
          <div className="stat-icon-bg">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12,6 12,12 16,14" />
            </svg>
          </div>
        </div>

        <div className="stat-card-v2 info">
          <div className="stat-content">
            <span className="stat-number">{totalClients}</span>
            <span className="stat-label">Clientes Odoo</span>
          </div>
          <div className="stat-icon-bg">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
            </svg>
          </div>
        </div>
      </section>

      {/* Sync Section */}
      <section className="sync-section">
        <div className="sync-info">
          <div className="sync-status">
            <span className="sync-label">Última sincronización:</span>
            <span className="sync-date">{formatDate(lastSyncInfo?.date || null)}</span>
            {lastSyncInfo && lastSyncInfo.newClients > 0 && (
              <span className="sync-new-badge">+{lastSyncInfo.newClients} nuevos</span>
            )}
          </div>
          <p className="sync-description">
            La sincronización busca solo clientes nuevos en Odoo desde la última actualización.
          </p>
          {stats && stats.usersWithUsername !== undefined && stats.usersWithUsername < stats.totalUsers && (
            <p className="sync-warning">
              {stats.totalUsers - stats.usersWithUsername} usuarios sin username
            </p>
          )}
        </div>
        <div className="sync-buttons">
          <button
            className={`btn-sync-v2 ${isSyncing ? 'syncing' : ''}`}
            onClick={handleSyncNew}
            disabled={isSyncing || isGeneratingUsernames}
          >
            {isSyncing ? (
              <>
                <svg className="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 11-6.219-8.56" />
                </svg>
                <span>
                  Buscando...
                  {syncProgress.total > 0 && ` (${syncProgress.progress}/${syncProgress.total})`}
                </span>
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10" />
                  <polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                </svg>
                <span>Sincronizar</span>
              </>
            )}
          </button>
          {stats && stats.usersWithUsername !== undefined && stats.usersWithUsername < stats.totalUsers && (
            <button
              className={`btn-generate-usernames ${isGeneratingUsernames ? 'generating' : ''}`}
              onClick={handleGenerateMissingUsernames}
              disabled={isSyncing || isGeneratingUsernames}
              title="Generar usernames para usuarios sin username"
            >
              {isGeneratingUsernames ? (
                <>
                  <svg className="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                  </svg>
                  <span>Generando...</span>
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                    <line x1="12" y1="11" x2="12" y2="17" />
                    <line x1="9" y1="14" x2="15" y2="14" />
                  </svg>
                  <span>Generar Usernames</span>
                </>
              )}
            </button>
          )}
        </div>
      </section>

      {/* Search & Filter Bar */}
      <section className="clients-toolbar">
        <div className="search-container">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Buscar por nombre, teléfono o correo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <div className="toolbar-info">
          <span className="results-count">
            {totalClients} cliente{totalClients !== 1 ? 's' : ''}
          </span>
        </div>
      </section>

      {/* Clients List */}
      <section className="clients-section">
        {isLoading ? (
          <div className="loading-container">
            <svg className="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            <span>Cargando clientes...</span>
          </div>
        ) : clients.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="17" y1="11" x2="23" y2="11" />
            </svg>
            <p>No se encontraron clientes</p>
          </div>
        ) : (
          <div className="clients-grid-v2">
            {clients.map((client) => (
              <div
                key={client.id}
                className={`client-card-v2 ${client.hasUser ? (client.user?.is_active ? 'active' : 'inactive') : 'no-access'}`}
              >
                {/* Card Header */}
                <div className="card-header">
                  <div className="client-avatar-v2">
                    {getAvatarInitial(client)}
                  </div>
                  <div className="client-primary-info">
                    <h3 className={`client-name-v2 ${isPhoneName(client.name) ? 'no-name' : ''}`}>
                      {getDisplayName(client)}
                    </h3>
                    {client.hasUser && client.user?.username && (
                      <span className="client-username">@{client.user.username}</span>
                    )}
                  </div>
                  <div className="card-status">
                    {client.hasUser ? (
                      client.user?.is_active ? (
                        <span className="status-dot active" title="Activo"></span>
                      ) : (
                        <span className="status-dot inactive" title="Inactivo"></span>
                      )
                    ) : (
                      <span className="status-dot no-access" title="Sin acceso"></span>
                    )}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="card-contact">
                  {(client.phone || client.mobile) && (
                    <div className="contact-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                      </svg>
                      <span>{client.phone || client.mobile}</span>
                    </div>
                  )}
                  {client.email && (
                    <div className="contact-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      <span>{client.email}</span>
                    </div>
                  )}
                </div>

                {/* Card Actions */}
                <div className="card-actions">
                  {client.hasUser ? (
                    <>
                      {!client.user?.username && (
                        <button
                          className="btn-card btn-username"
                          onClick={() => handleGenerateUsername(client)}
                          title="Generar username"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                            <path d="M16 3l2 2-2 2" />
                          </svg>
                          Username
                        </button>
                      )}
                      <button
                        className="btn-card btn-password"
                        onClick={() => handleOpenPasswordModal(client)}
                        title="Cambiar contraseña"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                        Contraseña
                      </button>
                      <button
                        className={`btn-card ${client.user?.is_active ? 'btn-block' : 'btn-unblock'}`}
                        onClick={() => handleToggleAccess(client)}
                        title={client.user?.is_active ? 'Desactivar acceso' : 'Activar acceso'}
                      >
                        {client.user?.is_active ? (
                          <>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                            </svg>
                            Bloquear
                          </>
                        ) : (
                          <>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                              <polyline points="22,4 12,14.01 9,11.01" />
                            </svg>
                            Activar
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn-card btn-create-access"
                      onClick={() => handleCreateAccess(client)}
                      disabled={!client.phone && !client.mobile && !client.email}
                      title={!client.phone && !client.mobile && !client.email
                        ? 'Cliente sin datos de contacto'
                        : 'Crear acceso al sistema'
                      }
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                        <circle cx="8.5" cy="7" r="4" />
                        <line x1="20" y1="8" x2="20" y2="14" />
                        <line x1="23" y1="11" x2="17" y2="11" />
                      </svg>
                      Crear Acceso
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15,18 9,12 15,6" />
              </svg>
            </button>
            <div className="pagination-info">
              Página {currentPage} de {totalPages}
            </div>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9,18 15,12 9,6" />
              </svg>
            </button>
          </div>
        )}
      </section>
        </>
      )}

      {/* ===== TAB: EJECUTIVOS ===== */}
      {activeTab === 'ejecutivos' && (
        <>
          {/* Barra de búsqueda y actualización */}
          <section className="ejecutivos-toolbar">
            <div className="search-container ejecutivos-search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Buscar ejecutivo..."
                value={ejecutivosSearch}
                onChange={(e) => setEjecutivosSearch(e.target.value)}
              />
              {ejecutivosSearch && (
                <button className="clear-search" onClick={() => setEjecutivosSearch('')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            <button
              className="btn-refresh"
              onClick={loadEjecutivosConClientes}
              disabled={isLoadingEjecutivos}
            >
              {isLoadingEjecutivos ? (
                <svg className="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 11-6.219-8.56" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10" />
                  <polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                </svg>
              )}
              Actualizar desde Odoo
            </button>
          </section>

          {/* Ejecutivos de Odoo con sus clientes */}
          <section className="ejecutivos-section">
            <div className="section-header">
              <h2>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Ejecutivos con Clientes Asignados ({ejecutivosConClientes.length})
              </h2>
            </div>

            {isLoadingEjecutivos ? (
              <div className="loading-container">
                <svg className="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 11-6.219-8.56" />
                </svg>
                <span>Cargando ejecutivos desde Odoo...</span>
              </div>
            ) : ejecutivosConClientes.length === 0 ? (
              <div className="empty-state">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <p>No se encontraron ejecutivos con clientes asignados en Odoo</p>
              </div>
            ) : (
              <div className="ejecutivos-grid">
                {ejecutivosConClientes.map((ejecutivo) => (
                  <div
                    key={ejecutivo.odooUserId}
                    className={`ejecutivo-card ${ejecutivo.hasAccount ? (ejecutivo.account?.is_active ? 'active' : 'inactive') : 'no-access'}`}
                  >
                    <div className="card-header">
                      <div className="ejecutivo-avatar">
                        {ejecutivo.odooName.charAt(0).toUpperCase()}
                      </div>
                      <div className="ejecutivo-primary-info">
                        <h3>{ejecutivo.odooName}</h3>
                        {ejecutivo.hasAccount && ejecutivo.account?.username && (
                          <span className="ejecutivo-username">@{ejecutivo.account.username}</span>
                        )}
                        <span className="clientes-badge">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                          </svg>
                          {ejecutivo.clientesCount} clientes
                        </span>
                      </div>
                      <div className="card-status">
                        {ejecutivo.hasAccount ? (
                          ejecutivo.account?.is_active ? (
                            <span className="status-dot active" title="Cuenta activa"></span>
                          ) : (
                            <span className="status-dot inactive" title="Cuenta inactiva"></span>
                          )
                        ) : (
                          <span className="status-dot no-access" title="Sin cuenta en el sistema"></span>
                        )}
                      </div>
                    </div>

                    <div className="card-contact">
                      {ejecutivo.odooEmail && (
                        <div className="contact-item">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                            <polyline points="22,6 12,13 2,6" />
                          </svg>
                          <span>{ejecutivo.odooEmail}</span>
                        </div>
                      )}
                      {ejecutivo.hasAccount && (
                        <div className="contact-item">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12,6 12,12 16,14" />
                          </svg>
                          <span>Último acceso: {formatDate(ejecutivo.account?.last_login || null)}</span>
                        </div>
                      )}
                      {!ejecutivo.hasAccount && (
                        <div className="contact-item warning-item">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                          </svg>
                          <span>Sin cuenta de acceso al sistema</span>
                        </div>
                      )}
                    </div>

                    <div className="card-actions">
                      {ejecutivo.hasAccount ? (
                        <>
                          <button
                            className="btn-card btn-view-clientes"
                            onClick={() => handleViewClientes(ejecutivo)}
                            title="Ver clientes asignados"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                              <circle cx="9" cy="7" r="4" />
                              <path d="M23 21v-2a4 4 0 00-3-3.87m-4-12a4 4 0 010 7.75" />
                            </svg>
                            Ver Clientes
                          </button>
                          <button
                            className="btn-card btn-password"
                            onClick={() => handleOpenEjecutivoPasswordModal(ejecutivo)}
                            title="Cambiar contraseña"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0110 0v4" />
                            </svg>
                            Contraseña
                          </button>
                          <button
                            className={`btn-card ${ejecutivo.account?.is_active ? 'btn-block' : 'btn-unblock'}`}
                            onClick={() => handleToggleEjecutivoStatus(ejecutivo)}
                            title={ejecutivo.account?.is_active ? 'Desactivar' : 'Activar'}
                          >
                            {ejecutivo.account?.is_active ? (
                              <>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                                </svg>
                                Bloquear
                              </>
                            ) : (
                              <>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                                  <polyline points="22,4 12,14.01 9,11.01" />
                                </svg>
                                Activar
                              </>
                            )}
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn-card btn-create-access full-width"
                          onClick={() => handleOpenCreateEjecutivoFromOdoo(ejecutivo)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                            <circle cx="8.5" cy="7" r="4" />
                            <line x1="20" y1="8" x2="20" y2="14" />
                            <line x1="23" y1="11" x2="17" y2="11" />
                          </svg>
                          Crear Cuenta de Acceso
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Modal: Cambiar Contraseña de Cliente */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title={`Cambiar contraseña`}
      >
        <div className="modal-form">
          <div className="modal-client-info">
            <div className="modal-avatar">
              {selectedClient?.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h4>{selectedClient?.name}</h4>
              {selectedClient?.user?.username && (
                <span className="modal-username">@{selectedClient.user.username}</span>
              )}
            </div>
          </div>
          <div className="form-group">
            <label>Nueva contraseña</label>
            <input
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              placeholder="Mínimo 6 caracteres"
            />
          </div>
          <div className="form-group">
            <label>Confirmar contraseña</label>
            <input
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              placeholder="Repetir contraseña"
            />
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={passwordForm.mustChangeOnLogin}
                onChange={(e) => setPasswordForm({ ...passwordForm, mustChangeOnLogin: e.target.checked })}
              />
              Solicitar cambio en próximo inicio de sesión
            </label>
          </div>
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSavePassword}>
              Guardar Contraseña
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal: Crear Ejecutivo */}
      <Modal
        isOpen={showCreateEjecutivoModal}
        onClose={() => setShowCreateEjecutivoModal(false)}
        title="Crear Cuenta de Ejecutivo"
      >
        <div className="modal-form">
          <div className="modal-client-info">
            <div className="modal-avatar ejecutivo">
              {selectedSalesperson?.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h4>{selectedSalesperson?.name}</h4>
              <span className="modal-username">
                {(selectedSalesperson as any)?.clientesCount || 0} clientes asignados
              </span>
            </div>
          </div>
          <p className="modal-info-text">
            Se generará un @username único automáticamente basado en el nombre.
          </p>
          <div className="form-group">
            <label>Contraseña de acceso</label>
            <input
              type="password"
              value={createEjecutivoForm.password}
              onChange={(e) => setCreateEjecutivoForm({ ...createEjecutivoForm, password: e.target.value })}
              placeholder="Ingresa una contraseña"
            />
            <span className="form-hint">Si dejas vacío se usará: Temporal123!</span>
          </div>
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setShowCreateEjecutivoModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleCreateEjecutivo}>
              Crear Acceso
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal: Cambiar Contraseña de Ejecutivo */}
      <Modal
        isOpen={showEjecutivoPasswordModal}
        onClose={() => setShowEjecutivoPasswordModal(false)}
        title="Cambiar contraseña de Ejecutivo"
      >
        <div className="modal-form">
          <div className="modal-client-info">
            <div className="modal-avatar ejecutivo">
              {selectedEjecutivoConClientes?.odooName.charAt(0).toUpperCase()}
            </div>
            <div>
              <h4>{selectedEjecutivoConClientes?.odooName}</h4>
              {selectedEjecutivoConClientes?.account?.username && (
                <span className="modal-username">@{selectedEjecutivoConClientes.account.username}</span>
              )}
            </div>
          </div>
          <div className="form-group">
            <label>Nueva contraseña</label>
            <input
              type="password"
              value={ejecutivoPasswordForm.newPassword}
              onChange={(e) => setEjecutivoPasswordForm({ ...ejecutivoPasswordForm, newPassword: e.target.value })}
              placeholder="Mínimo 6 caracteres"
            />
          </div>
          <div className="form-group">
            <label>Confirmar contraseña</label>
            <input
              type="password"
              value={ejecutivoPasswordForm.confirmPassword}
              onChange={(e) => setEjecutivoPasswordForm({ ...ejecutivoPasswordForm, confirmPassword: e.target.value })}
              placeholder="Repetir contraseña"
            />
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={ejecutivoPasswordForm.mustChangeOnLogin}
                onChange={(e) => setEjecutivoPasswordForm({ ...ejecutivoPasswordForm, mustChangeOnLogin: e.target.checked })}
              />
              Solicitar cambio en próximo inicio de sesión
            </label>
          </div>
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setShowEjecutivoPasswordModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSaveEjecutivoPassword}>
              Guardar Contraseña
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal: Ver Clientes del Ejecutivo */}
      <Modal
        isOpen={showClientesModal}
        onClose={() => setShowClientesModal(false)}
        title={`Clientes de ${selectedEjecutivoConClientes?.odooName || 'Ejecutivo'}`}
      >
        <div className="modal-clientes">
          {isLoadingClientes ? (
            <div className="loading-container small">
              <svg className="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 11-6.219-8.56" />
              </svg>
              <span>Cargando clientes...</span>
            </div>
          ) : clientesDelEjecutivo.length === 0 ? (
            <div className="empty-state small">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
              </svg>
              <p>No se encontraron clientes asignados</p>
            </div>
          ) : (
            <>
              <div className="clientes-list">
                {clientesDelEjecutivo.map((cliente) => (
                  <div key={cliente.id} className={`cliente-item ${cliente.hasUser ? 'has-user' : ''}`}>
                    <div className="cliente-avatar-small">
                      {cliente.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="cliente-info">
                      <h4>{cliente.name}</h4>
                      <div className="cliente-details">
                        {cliente.phone && <span>{cliente.phone}</span>}
                        {cliente.email && <span>{cliente.email}</span>}
                      </div>
                    </div>
                    <div className="cliente-status">
                      {cliente.hasUser ? (
                        <span className="badge badge-success">Con acceso</span>
                      ) : (
                        <span className="badge badge-warning">Sin acceso</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {clientesTotalPages > 1 && (
                <div className="pagination modal-pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => selectedEjecutivoConClientes?.account && loadClientesDelEjecutivo(selectedEjecutivoConClientes.account.id, clientesPage - 1)}
                    disabled={clientesPage === 1}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15,18 9,12 15,6" />
                    </svg>
                  </button>
                  <div className="pagination-info">
                    Página {clientesPage} de {clientesTotalPages}
                  </div>
                  <button
                    className="pagination-btn"
                    onClick={() => selectedEjecutivoConClientes?.account && loadClientesDelEjecutivo(selectedEjecutivoConClientes.account.id, clientesPage + 1)}
                    disabled={clientesPage === clientesTotalPages}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9,18 15,12 9,6" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setShowClientesModal(false)}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanel;
